import { useMemo, useState } from "react";
import { toast } from "sonner";
import { logContact } from "@/lib/log-contact";

const APPLIANCES = [
  "Refrigerator",
  "Washer",
  "Dryer",
  "Dishwasher",
  "Oven / Stove / Cooktop",
  "Microwave",
] as const;

type TimeWindow = "morning" | "afternoon" | "evening";

const TIME_WINDOWS: { value: TimeWindow; label: string; hint: string }[] = [
  { value: "morning", label: "Morning", hint: "8am – 12pm" },
  { value: "afternoon", label: "Afternoon", hint: "12pm – 4pm" },
  { value: "evening", label: "Evening", hint: "4pm – 8pm" },
];

function todayISO(offsetDays = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return d.toISOString().slice(0, 10);
}

export function BookingForm() {
  const [submitting, setSubmitting] = useState(false);
  const [timeWindow, setTimeWindow] = useState<TimeWindow>("morning");
  const minDate = useMemo(() => todayISO(0), []);
  const maxDate = useMemo(() => todayISO(30), []);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    const form = e.currentTarget;
    const data = new FormData(form);

    const payload = {
      name: String(data.get("name") || "").trim().slice(0, 120),
      phone: String(data.get("phone") || "").trim().slice(0, 40),
      address: String(data.get("address") || "").trim().slice(0, 300),
      appliance: String(data.get("appliance") || "").slice(0, 80),
      problem: String(data.get("problem") || "").trim().slice(0, 2000),
      preferredDate: String(data.get("preferredDate") || ""),
      timeWindow,
    };

    try {
      const res = await fetch("/api/public/booking", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error || `Request failed (${res.status})`);
      }

      await logContact("booking", payload);

      toast.success("Booking received! We'll confirm your visit shortly.", {
        duration: 6000,
      });
      form.reset();
      setTimeWindow("morning");
    } catch (err) {
      console.error(err);
      toast.error(
        "Couldn't submit your request. Please try again or call us directly.",
        { duration: 6000 },
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="surface-elevated p-6 sm:p-8 space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-muted-foreground">Name</span>
          <input
            name="name"
            required
            placeholder="Your name"
            className="w-full rounded-xl border border-border bg-background px-4 py-3 text-foreground placeholder:text-muted-foreground/70 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
        </label>
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-muted-foreground">Phone</span>
          <input
            name="phone"
            required
            type="tel"
            placeholder="(555) 555-5555"
            className="w-full rounded-xl border border-border bg-background px-4 py-3 text-foreground placeholder:text-muted-foreground/70 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
        </label>
      </div>

      <label className="block">
        <span className="mb-1.5 block text-sm font-medium text-muted-foreground">Service address</span>
        <input
          name="address"
          required
          placeholder="Street, City, ZIP"
          className="w-full rounded-xl border border-border bg-background px-4 py-3 text-foreground placeholder:text-muted-foreground/70 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/40"
        />
      </label>

      <label htmlFor="booking-appliance" className="block">
        <span className="mb-1.5 block text-sm font-medium text-muted-foreground">Appliance</span>
        <select
          id="booking-appliance"
          name="appliance"
          required
          aria-label="Appliance"
          defaultValue=""
          className="block h-[52px] w-full appearance-none rounded-xl border border-border bg-background bg-[length:1.25rem] bg-[right_1rem_center] bg-no-repeat px-4 py-3 pr-12 text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/40"
          style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>\")" }}
        >
          <option value="" disabled>Select appliance</option>
          {APPLIANCES.map((a) => (
            <option key={a} value={a} className="bg-background text-foreground">{a}</option>
          ))}
        </select>
      </label>

      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-muted-foreground">Preferred date</span>
          <input
            name="preferredDate"
            required
            type="date"
            min={minDate}
            max={maxDate}
            defaultValue={minDate}
            className="block w-full min-w-0 max-w-full appearance-none rounded-xl border border-border bg-background px-4 py-3 text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/40 [&::-webkit-date-and-time-value]:text-left"
          />
        </label>
        <div className="block">
          <span className="mb-1.5 block text-sm font-medium text-muted-foreground">Time window</span>
          <div className="grid grid-cols-3 gap-2">
            {TIME_WINDOWS.map((w) => {
              const active = timeWindow === w.value;
              return (
                <button
                  key={w.value}
                  type="button"
                  onClick={() => setTimeWindow(w.value)}
                  className={`rounded-xl border px-2 py-2.5 text-center transition-all duration-200 ${
                    active
                      ? "border-primary bg-primary/15 text-foreground shadow-lg shadow-primary/20"
                      : "border-border bg-background text-muted-foreground hover:border-foreground/30 hover:text-foreground"
                  }`}
                  aria-pressed={active}
                >
                  <span className="block text-sm font-semibold">{w.label}</span>
                  <span className="block text-[11px] opacity-75">{w.hint}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <label className="block">
        <span className="mb-1.5 block text-sm font-medium text-muted-foreground">Problem</span>
        <textarea
          name="problem"
          required
          rows={4}
          placeholder="Tell us what's happening (e.g., not cooling, leaking, loud noise)"
          className="w-full resize-none rounded-xl border border-border bg-background px-4 py-3 text-foreground placeholder:text-muted-foreground/70 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/40"
        />
      </label>

      <button
        type="submit"
        disabled={submitting}
        className="btn-accent w-full rounded-xl px-6 py-4 text-base font-semibold disabled:opacity-60 disabled:hover:translate-y-0"
      >
        {submitting ? "Sending…" : "Book a Visit"}
      </button>
      <p className="text-center text-xs text-muted-foreground">
        We usually reply within minutes. By submitting you agree to be contacted.
      </p>
    </form>
  );
}
